import React, { useState, useEffect, useRef, useCallback } from 'react';
import { usePincodeLookup } from '../hooks/usePincodeLookup.ts';
import { formatPincode, PincodeData } from '../services/pincodeService.ts';

interface PincodeInputProps {
  value: string;
  onChange: (pincode: string) => void;
  onCityStateChange?: (city: string, state: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  error?: string; // External validation error
}

const PincodeInput: React.FC<PincodeInputProps> = ({
  value,
  onChange,
  onCityStateChange,
  placeholder = "Enter 6-digit pincode",
  className = "",
  disabled = false,
  required = false,
  error: externalError,
}) => {
  const { lookupPincode, pincodeData, loading, error, isValid, clearData } = usePincodeLookup();
  const [showSuggestions, setShowSuggestions] = useState(false);
  const prevPincodeDataRef = useRef<PincodeData | null>(null);
  const onCityStateChangeRef = useRef(onCityStateChange);

  // Update the ref whenever the callback changes
  useEffect(() => {
    onCityStateChangeRef.current = onCityStateChange;
  }, [onCityStateChange]);

  // Handle pincode input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const formattedValue = formatPincode(inputValue);
    onChange(formattedValue);
    
    // Clear previous data when user types
    if (pincodeData) {
      clearData();
    }
  };

  // Handle automatic lookup
  const handleLookup = useCallback(async (pincode?: string) => {
    const pincodeToLookup = pincode || value;
    if (pincodeToLookup.length === 6) {
      await lookupPincode(pincodeToLookup);
    }
  }, [value, lookupPincode]);

  // Auto-lookup when pincode is complete (debounced)
  useEffect(() => {
    if (value.length === 6) {
      const timeoutId = setTimeout(() => {
        handleLookup(value);
      }, 500); // 500ms debounce

      return () => clearTimeout(timeoutId);
    }
  }, [value, handleLookup]);

  // Notify parent component when city/state data changes
  useEffect(() => {
    if (pincodeData && onCityStateChangeRef.current) {
      // Only call the callback if the pincode data has actually changed
      const prevData = prevPincodeDataRef.current;
      if (!prevData || prevData.pincode !== pincodeData.pincode || 
          prevData.city !== pincodeData.city || prevData.state !== pincodeData.state) {
        onCityStateChangeRef.current(pincodeData.city, pincodeData.state);
        prevPincodeDataRef.current = pincodeData;
      }
    }
  }, [pincodeData]);

  // Handle input focus/blur for suggestions
  const handleFocus = () => {
    if (pincodeData) {
      setShowSuggestions(true);
    }
  };

  const handleBlur = () => {
    // Delay hiding suggestions to allow clicking on them
    setTimeout(() => setShowSuggestions(false), 200);
  };

  return (
    <div className="relative">
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          maxLength={6}
          disabled={disabled}
          required={required}
          className={`w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
            error || externalError ? 'border-red-300 focus:ring-red-500' : ''
          } ${isValid && !error && !externalError ? 'border-green-300 focus:ring-green-500' : ''} ${className}`}
        />
        
        {/* Loading spinner */}
        {loading && (
          <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
          </div>
        )}
        
        {/* Success checkmark */}
        {pincodeData && !loading && !error && (
          <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        )}
        
        {/* Error icon */}
        {(error || externalError) && !loading && (
          <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        )}
      </div>

      {/* Error message */}
      {(error || externalError) && (
        <div className="mt-2 flex items-center text-sm text-red-600">
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {externalError || error}
        </div>
      )}

      {/* Success message with city/state info */}
      {pincodeData && !showSuggestions && !loading && (
        <div className="mt-2 flex items-center text-sm text-green-600">
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          {pincodeData.city}, {pincodeData.state}
        </div>
      )}

      {/* Suggestions dropdown */}
      {showSuggestions && pincodeData && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
          <div className="p-3 border-b border-gray-100">
            <div className="text-sm font-medium text-gray-900">
              Pincode: {pincodeData.pincode}
            </div>
            <div className="text-sm text-gray-600 mt-1">
              {pincodeData.city}, {pincodeData.state}
            </div>
            {pincodeData.district && (
              <div className="text-xs text-gray-500 mt-1">
                District: {pincodeData.district}
              </div>
            )}
          </div>
          <div className="p-2">
            <button
              type="button"
              onClick={() => setShowSuggestions(false)}
              className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-md transition-colors duration-200"
            >
              Use this location
            </button>
          </div>
        </div>
      )}

      {/* Format hint */}
      {value.length > 0 && value.length < 6 && !error && (
        <div className="mt-1 text-xs text-gray-500">
          Enter {6 - value.length} more digits
        </div>
      )}
    </div>
  );
};

export default PincodeInput;
